import axios, { AxiosResponse, AxiosRequestConfig, Method } from 'axios';
import { SpaceshipApiError } from '../common/errors';
import { getToken } from './auth';

export interface RequestConfig {
  method: Method;
  path?: string;
  url?: string;
  data?: any;
  params?: any;
  headers?: Record<string, string>;
  isPublic?: boolean;
}

export async function apiService(request: RequestConfig): Promise<AxiosResponse> {
  const { headers, isPublic } = request;
  const token = !isPublic ? getToken() : null;
  const config = {
    headers: {
      'Content-Type': 'application/json',
      'x-locale': 'en-HK',
      ...headers,
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  };
  const { method, path } = request;
  const url = request.url || `${process.env.REACT_APP_API_BASE_URL}${path}`;

  return axios({
    ...config,
    data: request.data,
    params: request.params,
    method,
    url,
  } as AxiosRequestConfig);
}

class API {
  private service;

  constructor() {
    this.service = axios.create({
      baseURL: process.env.NEXT_PUBLIC_API,
      headers: {
        platform: 'web',
      },
    });
  }

  public setAccessToken(token: string) {
    this.service.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  public unsetAccessToken() {
    this.service.defaults.headers.common['Authorization'] = null;
  }

  public async get<T>(url: string, params = {}): Promise<T> {
    try {
      const res = await this.service.get(url, params);
      const { success, data, code, message } = res.data;
      if (!success) throw new SpaceshipApiError(code, message);
      return data;
    } catch (err: any) {
      if (err?.response?.status === 500) console.error(err);
      return Promise.reject(err);
    }
  }

  public async post<T>(url: string, params = {}): Promise<T> {
    try {
      const res = await this.service.post(url, params);
      const { success, data, code, message } = res.data;
      if (!success) throw new SpaceshipApiError(code, message);
      return data;
    } catch (err: any) {
      if (err?.response?.status === 500) console.error(err);
      return Promise.reject(err);
    }
  }

  public async delete<T>(url: string, params?: any): Promise<T> {
    try {
      const res = await this.service.delete(url, params ? { data: params } : {});
      const { success, data, code, message } = res.data;
      if (!success) throw new SpaceshipApiError(code, message);
      return data;
    } catch (err: any) {
      if (err?.response?.status === 500) console.error(err);
      return Promise.reject(err);
    }
  }
}

export default new API();
